import { useEffect, useRef, useState } from "react";

import { CloseIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Link as RouterLink } from "react-router-dom";

import PhHistoryGraph from "./PhHistoryGraph";

import { fetchGroupMembershipId } from "~/api/members";
import { AuthenticationRequiredError, PhHistoryEntry, deletePhAnalysis, fetchPhHistory } from "~/api/ph";
import { useAppState } from "~/hooks/useAppState";
import { formatDateTime } from "~/utils/date";

export const PhHistoryTabView = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentMember } = useAppState("currentMember");

  const [history, setHistory] = useState<PhHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<PhHistoryEntry | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Colors that respect the color mode
  const borderColor = useColorModeValue("green.200", "green.700");
  const bgColor = useColorModeValue("white", "gray.800");

  const loadHistory = async () => {
    if (!isLoaded) return;

    try {
      if (!currentMember) {
        setError("Please select a member to view pH history");
        setLoading(false);
        return;
      }

      setLoading(true);

      // Get the group membership ID first
      const groupMemberId = await fetchGroupMembershipId(currentMember.id);

      if (!groupMemberId) {
        setError("Could not find group membership for this member");
        setLoading(false);
        return;
      }

      const data = await fetchPhHistory(groupMemberId);
      setHistory(data);
      setError(null);
    } catch (err) {
      if (err instanceof AuthenticationRequiredError) {
        setError("Please log in to view your pH history");
      } else {
        setError(err instanceof Error ? err.message : "An error occurred loading your pH history");
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "An error occurred loading your pH history",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isLoaded, currentMember]);

  const handleDeleteClick = (entry: PhHistoryEntry) => {
    setSelectedEntry(entry);
    onOpen();
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntry) return;

    if (!currentMember) {
      toast({
        title: "Error",
        description: "No member selected",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      onClose();
      return;
    }

    setIsDeleting(true);
    try {
      // Get the group membership ID first
      const groupMemberId = await fetchGroupMembershipId(currentMember.id);

      if (!groupMemberId) {
        toast({
          title: "Error",
          description: "Could not find group membership for this member",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        setIsDeleting(false);
        onClose();
        return;
      }

      const result = await deletePhAnalysis(selectedEntry.id, groupMemberId);

      if (result.success) {
        toast({
          title: "Success",
          description: "pH analysis entry deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true
        });

        // Refresh the history list to remove the deleted entry
        await loadHistory();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to delete pH analysis entry",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "An error occurred deleting the entry",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  if (!isLoaded || loading) {
    return (
      <Flex justify="center" my={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!isSignedIn) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="md" mb={4}>
          Authentication Required
        </Heading>
        <Text mb={4}>
          Please{" "}
          <Link as={RouterLink} to="/signin" color="blue.500" fontWeight="bold">
            log in
          </Link>{" "}
          to view your pH history.
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="md" mb={4} color="red.500">
          Error
        </Heading>
        <Text>{error}</Text>
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="md" mb={4}>
          No pH History Found
        </Heading>
        <Text mb={4}>You haven't saved any pH analysis data yet.</Text>
        <Button as={RouterLink} to="/phanalysis" colorScheme="blue">
          Go to pH Analysis
        </Button>
      </Box>
    );
  }

  // Sort history by testing date (newest to oldest)
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.testingDate).getTime() - new Date(a.testingDate).getTime()
  );

  return (
    <Box p={4}>
      {/* pH Trends Graph - Now at the top */}
      <Box borderWidth="1px" borderRadius="lg" p={4} mb={6} boxShadow="sm" borderColor={borderColor} bg={bgColor}>
        <PhHistoryGraph historyData={sortedHistory} />
      </Box>

      {/* pH Analysis History Table - Now below the graph with compact style */}
      <Heading size="md" mb={4}>
        Your pH Analysis History
      </Heading>
      <Flex justifyContent="center">
        <Box
          width="50%"
          maxHeight="350px"
          overflowY="auto"
          overflowX="auto"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="sm"
          borderColor={borderColor}
          minWidth="400px"
        >
          <Table variant="simple" size="sm">
            <Thead position="sticky" top={0} bg={bgColor} zIndex={1} boxShadow="sm">
              <Tr>
                <Th>Test Date & Time</Th>
                <Th>Saliva pH</Th>
                <Th>Urine pH</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedHistory.map((entry) => (
                <Tr key={entry.id}>
                  <Td whiteSpace="nowrap">{formatDateTime(entry.testingDate)}</Td>
                  <Td>{entry.salivaPh}</Td>
                  <Td>{entry.urinePh}</Td>
                  <Td>
                    <Flex gap={2}>
                      <Button size="xs" colorScheme="blue" as={RouterLink} to={`/phanalysis?id=${entry.id}`}>
                        View
                      </Button>
                      <Tooltip label={`Delete pH analysis entry for ${formatDateTime(entry.testingDate)}`}>
                        <IconButton
                          aria-label="Delete entry"
                          icon={<CloseIcon />}
                          size="xs"
                          bg="red.500"
                          color="white"
                          border="none"
                          _hover={{
                            bg: "red.400",
                            transform: "scale(1.1)",
                            transition: "all 0.2s ease"
                          }}
                          onClick={() => handleDeleteClick(entry)}
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Flex>

      {/* Confirmation Dialog */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete pH Analysis Entry
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this pH Analysis entry? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Keep
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PhHistoryTabView;
